import React, { useState, useRef, useEffect } from 'react';
import { 
  Building2, ShieldCheck, Search, Calculator, Package, 
  Truck, TrendingUp, Users, Zap, CheckCircle, ArrowRight, Sparkles, Star
} from 'lucide-react';

const skills = [
  { icon: Building2, title: 'Открыть компанию', desc: 'Правильная форма, страна', color: 'amber' },
  { icon: ShieldCheck, title: 'Пройти верификацию', desc: 'Аккаунт без блокировки', color: 'emerald' },
  { icon: Search, title: 'Находить товары', desc: 'Которые уже покупают', color: 'blue' },
  { icon: Calculator, title: 'Считать прибыль', desc: 'Комиссии, реальная маржа', color: 'violet' },
  { icon: Package, title: 'Готовить товар', desc: 'Маркировка по правилам', color: 'rose' },
  { icon: Truck, title: 'Отправлять на склад', desc: 'Логистика от А до Я', color: 'cyan' },
  { icon: TrendingUp, title: 'Выигрывать Buy Box', desc: 'Больше продаж', color: 'amber' },
  { icon: Users, title: 'Работать с поставщиками', desc: 'Переговоры, скидки', color: 'emerald' },
  { icon: Zap, title: 'Масштабироваться', desc: 'Больше прибыли', color: 'violet' },
];

const colorConfig = {
  amber: { gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', shadow: 'shadow-amber-500/20' },
  emerald: { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', shadow: 'shadow-emerald-500/20' },
  blue: { gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', shadow: 'shadow-blue-500/20' },
  violet: { gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-400', shadow: 'shadow-violet-500/20' },
  rose: { gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-500/10', border: 'border-rose-500/30', text: 'text-rose-400', shadow: 'shadow-rose-500/20' },
  cyan: { gradient: 'from-cyan-500 to-teal-600', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400', shadow: 'shadow-cyan-500/20' },
};

const CourseGoals = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="relative py-20 sm:py-28 overflow-hidden bg-[#030304]" 
      id="skills"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/3 left-0 w-[600px] h-[600px] bg-emerald-500/[0.02] rounded-full blur-[150px]" />
        <div className="absolute bottom-1/3 right-0 w-[500px] h-[500px] bg-violet-500/[0.02] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 mb-6 backdrop-blur-xl">
            <Star className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300 font-bold text-sm tracking-wide">РЕЗУЛЬТАТ</span>
          </div>
          
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-4">
            <span className="text-white">После курса </span>
            <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">вы сможете</span>
          </h2>
          <p className="text-xl text-zinc-400">Конкретные навыки. Практика, не теория.</p>
        </div>

        {/* Skills Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {skills.map((skill, index) => {
            const Icon = skill.icon;
            const colors = colorConfig[skill.color];
            
            return (
              <div
                key={index}
                className={`group relative transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                {/* Card - static, not interactive */}
                <div className={`relative h-full p-4 sm:p-5 rounded-2xl border-2 backdrop-blur-sm ${colors.bg} ${colors.border}`}>
                  
                  <div className="flex items-start gap-3 sm:gap-4">
                    {/* Icon */}
                    <div className={`relative w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${colors.gradient} shadow-lg ${colors.shadow}`}>
                      <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                    </div>
                    
                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-black mb-0.5 sm:mb-1 text-white">
                        {skill.title}
                      </h3>
                      <p className={`text-xs sm:text-sm ${colors.text}`}>
                        {skill.desc}
                      </p>
                    </div>
                    
                    {/* Check */}
                    <CheckCircle className={`w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 ${colors.text}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <a 
            href="#pricing"
            className="group inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black text-xl rounded-2xl shadow-2xl shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-1 active:scale-[0.98] transition-all duration-300"
            style={{ touchAction: 'manipulation' }}
          >
            <span>Получить эти навыки</span>
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default CourseGoals;
